import { type RefObject, useEffect } from 'react';

import { elementFromEventTarget, getDocument } from '@/lib/utils';

export interface UseClickOutsideOptions {
  enabled?: boolean;
  excludeSelectors?: string[];
  capture?: boolean;
  dismissOnFocus?: boolean;
  shouldIgnoreTarget?: (target: Element | null) => boolean;
  onDismiss: (outsideTarget: Element | null) => void;
}

function isDismissTarget<T extends Node>(
  ref: RefObject<T | null>,
  target: EventTarget | null,
  excludeSelectors: string[],
  shouldIgnoreTarget?: (target: Element | null) => boolean,
): boolean {
  const elementTarget = elementFromEventTarget(target);
  if (!elementTarget) {
    return false;
  }
  if (excludeSelectors.some((selector) => elementTarget.closest(selector))) {
    return false;
  }
  if (shouldIgnoreTarget?.(elementTarget)) {
    return false;
  }
  if (ref.current?.contains(elementTarget)) {
    return false;
  }
  return true;
}

export function useClickOutside<T extends Node>(
  ref: RefObject<T | null>,
  {
    enabled = true,
    excludeSelectors = [],
    capture = false,
    dismissOnFocus = false,
    shouldIgnoreTarget,
    onDismiss,
  }: UseClickOutsideOptions,
) {
  useEffect(() => {
    if (!enabled) return undefined;

    const doc = getDocument();
    if (!doc) return undefined;

    function handlePointerDown(event: PointerEvent) {
      if (!isDismissTarget(ref, event.target, excludeSelectors, shouldIgnoreTarget)) {
        return;
      }
      onDismiss(elementFromEventTarget(event.target));
    }

    function handleFocusIn(event: FocusEvent) {
      if (!isDismissTarget(ref, event.target, excludeSelectors, shouldIgnoreTarget)) {
        return;
      }
      onDismiss(elementFromEventTarget(event.target));
    }

    doc.addEventListener('pointerdown', handlePointerDown, capture);
    if (dismissOnFocus) {
      doc.addEventListener('focusin', handleFocusIn, capture);
    }
    return () => {
      doc.removeEventListener('pointerdown', handlePointerDown, capture);
      if (dismissOnFocus) {
        doc.removeEventListener('focusin', handleFocusIn, capture);
      }
    };
  }, [capture, dismissOnFocus, enabled, excludeSelectors, onDismiss, ref, shouldIgnoreTarget]);
}
